// @flow
import * as React from 'react';

import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Display from 'components/Display';
import Divider from 'components/Divider';
import Glyph from 'components/Glyph';
import LayerTrigger from 'components/LayerTrigger';
import Menu from 'components/Menu';
import MenuButton from 'components/MenuButton';
import MenuItem from 'components/MenuItem';
import TextWithGlyph from 'components/TextWithGlyph';
import TextWithIcon from 'components/TextWithIcon';
import Tooltip from 'components/Tooltip';
// eslint-disable-next-line no-unused-vars
import type { Node } from 'React';

type Props = {
  onEdit: () => void,
  onDuplicate: () => void,
  onDelete: () => void,
  onChangeStatus: () => void,
  isActivatable: boolean,
  canEdit: boolean,
  messageIsActive: boolean,
  creatorOrgName: ?string,
};

export default class AdminAutoMessageDetailViewActions extends React.PureComponent<Props, *> {
  render() {
    const {
      messageIsActive,
      onDuplicate,
      onChangeStatus,
      onDelete,
      onEdit,
      isActivatable,
      canEdit,
      creatorOrgName,
    } = this.props;

    function wrapInDisabledTooltip(component: React.Node) {
      const renderTooltip = (copy: string) => {
        return (
          <LayerTrigger noPointer layer={<Tooltip>{copy}</Tooltip>}>
            <span>{component}</span>
          </LayerTrigger>
        );
      };
      if (canEdit) {
        return renderTooltip(
          'It looks like not all of the required fields have been filled out yet. Click ‘Edit’ to complete and activate the message.'
        );
      }
      return renderTooltip(
        `Once an administrator${
          creatorOrgName ? ` from ${creatorOrgName}` : ' at the district level'
        } completes the message, it can be activated.`
      );
    }

    const changeStatusDisabled = !messageIsActive && !isActivatable;
    const changeStatusOnClick = isActivatable ? onChangeStatus : () => undefined;
    const changeStatusIcon = messageIsActive ? 'pause-circle' : 'play-circle';
    const changeStatusLabel = messageIsActive ? 'Pause' : 'Activate';

    function ChangeStatusMenuItem() {
      return (
        <MenuItem
          data-test="activate-action-item"
          disabled={changeStatusDisabled}
          onClick={changeStatusOnClick}>
          <TextWithIcon icon={changeStatusIcon}>{changeStatusLabel}</TextWithIcon>
        </MenuItem>
      );
    }

    function ChangeStatusButton() {
      return (
        <Button
          disabled={changeStatusDisabled}
          data-test="activate-button"
          onClick={changeStatusOnClick}>
          <TextWithIcon icon={changeStatusIcon}>{changeStatusLabel}</TextWithIcon>
        </Button>
      );
    }

    const menu = (
      <Menu>
        <Display show="1200">
          {changeStatusDisabled ? (
            wrapInDisabledTooltip(<ChangeStatusMenuItem />)
          ) : (
            <ChangeStatusMenuItem />
          )}
          {canEdit && (
            <MenuItem data-test="edit-action-item" onClick={onEdit}>
              <TextWithGlyph glyph="pencil-stroke">Edit</TextWithGlyph>
            </MenuItem>
          )}
        </Display>
        <MenuItem data-test="duplicate-action-item" onClick={onDuplicate}>
          <TextWithIcon icon="copy">Duplicate</TextWithIcon>
        </MenuItem>
        {canEdit && (
          <React.Fragment>
            <MenuItem>
              <Divider size="flush" />
            </MenuItem>
            <MenuItem data-test="delete-action-item" onClick={onDelete}>
              <TextWithGlyph glyph="trash">Delete</TextWithGlyph>
            </MenuItem>
          </React.Fragment>
        )}
      </Menu>
    );

    return (
      <Buttons>
        <Display hide="1200">
          {changeStatusDisabled ? (
            wrapInDisabledTooltip(<ChangeStatusButton />)
          ) : (
            <ChangeStatusButton />
          )}
        </Display>
        {canEdit && (
          <Display hide="1200">
            <Button data-test="edit-button" level="secondary" onClick={onEdit}>
              Edit
            </Button>
          </Display>
        )}
        <MenuButton
          data-test="actions-button"
          menu={menu}
          level="secondary"
          color="koala"
          alignment="right"
          aria-label="More actions">
          <Glyph name="ellipses" size="small" solo />
        </MenuButton>
      </Buttons>
    );
  }
}
